import * as React from 'react'
import {
  DropdownMenuBase as DropdownMenu,
  DropdownMenuTriggerBase as DropdownMenuTrigger,
  DropdownMenuContentBase as DropdownMenuContent,
  DropdownMenuItemBase as DropdownMenuItem,
  DropdownMenuCheckboxItemBase as DropdownMenuCheckboxItem,
  DropdownMenuRadioItemBase as DropdownMenuRadioItem,
  DropdownMenuLabelBase as DropdownMenuLabel,
  DropdownMenuSeparatorBase as DropdownMenuSeparator,
  DropdownMenuShortcutBase as DropdownMenuShortcut,
  DropdownMenuGroupBase as DropdownMenuGroup,
  DropdownMenuSubBase as DropdownMenuSub,
  DropdownMenuSubTriggerBase as DropdownMenuSubTrigger,
  DropdownMenuSubContentBase as DropdownMenuSubContent,
  DropdownMenuRadioGroupBase as DropdownMenuRadioGroup,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * Figma (node 89:189 — Dropdown Menu page):
 *
 * Menu (node 105:55):
 *   bg-popover, border border-border, rounded-lg (10px), shadow-md, p-1 (4px)
 *
 * Item (node 330:8549):
 *   rounded-md (8px), px-1.5 (6px), py-1 (4px), gap-1.5 (6px), text-sm (14px), max-h-7 (28px)
 *   Variants: Default, Shortcut, Destructive
 *   States: Default, Hover (bg-accent), Disabled (opacity-50)
 *   Destructive hover: bg-destructive/10
 *
 * Label (node 330:24589):
 *   text-xs (12px), font-medium, text-muted-foreground, px-1.5 py-1
 *
 * Separator (node 91:272):
 *   h-px bg-border, -mx-1 my-1
 *
 * SubTrigger (node 330:24002):
 *   Same as Item + ChevronRight icon, data-[state=open]:bg-accent
 */

/* ─── Root ─── */
/* Thin wrapper to avoid TDZ — circular dep means DropdownMenu is uninitialized at module scope */
const IntuiDropdownMenu = (props: React.ComponentProps<typeof DropdownMenu>) => <DropdownMenu {...props} />

/* ─── Trigger ─── */
const IntuiDropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuTrigger>
>((props, ref) => <DropdownMenuTrigger ref={ref} {...props} />)
IntuiDropdownMenuTrigger.displayName = 'IntuiDropdownMenuTrigger'

/* ─── Content ─── */
const IntuiDropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuContent
    ref={ref}
    className={cn('rounded-lg', className)}
    {...props}
  />
))
IntuiDropdownMenuContent.displayName = 'IntuiDropdownMenuContent'

/* ─── Item ─── */
export interface IntuiDropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuItem> {
  /** Render item with destructive (red) styling */
  variant?: 'default' | 'destructive'
  /** Optional icon rendered before the label */
  icon?: React.ReactNode
  /** Keyboard shortcut displayed on the right */
  shortcut?: string
}

const IntuiDropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  IntuiDropdownMenuItemProps
>(({ className, variant = 'default', icon, shortcut, children, ...props }, ref) => (
  <DropdownMenuItem
    ref={ref}
    className={cn(
      'rounded-md px-1.5 py-1 gap-1.5 text-sm',
      variant === 'destructive' &&
        'text-destructive focus:text-destructive focus:bg-destructive/10',
      className,
    )}
    {...props}
  >
    {icon && <span className="[&>svg]:size-4 [&>svg]:shrink-0">{icon}</span>}
    {children}
    {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
  </DropdownMenuItem>
))
IntuiDropdownMenuItem.displayName = 'IntuiDropdownMenuItem'

/* ─── Label ─── */
const IntuiDropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuLabel>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuLabel>
>(({ className, ...props }, ref) => (
  <DropdownMenuLabel
    ref={ref}
    className={cn('px-1.5 py-1 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
))
IntuiDropdownMenuLabel.displayName = 'IntuiDropdownMenuLabel'

/* ─── Separator ─── */
const IntuiDropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSeparator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSeparator>
>(({ className, ...props }, ref) => (
  <DropdownMenuSeparator ref={ref} className={cn('', className)} {...props} />
))
IntuiDropdownMenuSeparator.displayName = 'IntuiDropdownMenuSeparator'

/* ─── Sub ─── */
const IntuiDropdownMenuSub = (props: React.ComponentProps<typeof DropdownMenuSub>) => <DropdownMenuSub {...props} />

/* ─── SubTrigger ─── */
const IntuiDropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSubTrigger>
>(({ className, ...props }, ref) => (
  <DropdownMenuSubTrigger
    ref={ref}
    className={cn('rounded-md px-1.5 py-1 gap-1.5 text-sm', className)}
    {...props}
  />
))
IntuiDropdownMenuSubTrigger.displayName = 'IntuiDropdownMenuSubTrigger'

/* ─── SubContent ─── */
const IntuiDropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuSubContent
    ref={ref}
    className={cn('rounded-lg', className)}
    {...props}
  />
))
IntuiDropdownMenuSubContent.displayName = 'IntuiDropdownMenuSubContent'

/* ─── CheckboxItem ─── */
const IntuiDropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuCheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuCheckboxItem
    ref={ref}
    className={cn('rounded-md', className)}
    {...props}
  />
))
IntuiDropdownMenuCheckboxItem.displayName = 'IntuiDropdownMenuCheckboxItem'

/* ─── RadioGroup ─── */
const IntuiDropdownMenuRadioGroup = (props: React.ComponentProps<typeof DropdownMenuRadioGroup>) => <DropdownMenuRadioGroup {...props} />

/* ─── RadioItem ─── */
const IntuiDropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuRadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuRadioItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuRadioItem
    ref={ref}
    className={cn('rounded-md', className)}
    {...props}
  />
))
IntuiDropdownMenuRadioItem.displayName = 'IntuiDropdownMenuRadioItem'

/* ─── Group ─── */
const IntuiDropdownMenuGroup = (props: React.ComponentProps<typeof DropdownMenuGroup>) => <DropdownMenuGroup {...props} />

/* ─── Shortcut (convenience re-export) ─── */
const IntuiDropdownMenuShortcut = (props: React.ComponentProps<typeof DropdownMenuShortcut>) => <DropdownMenuShortcut {...props} />

export {
  IntuiDropdownMenu,
  IntuiDropdownMenuTrigger,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuLabel,
  IntuiDropdownMenuSeparator,
  IntuiDropdownMenuSub,
  IntuiDropdownMenuSubTrigger,
  IntuiDropdownMenuSubContent,
  IntuiDropdownMenuCheckboxItem,
  IntuiDropdownMenuRadioGroup,
  IntuiDropdownMenuRadioItem,
  IntuiDropdownMenuGroup,
  IntuiDropdownMenuShortcut,
}
